import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, homeForRole } from "@/lib/auth";
import "@/index.css";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import InfluencerSignup from "@/pages/InfluencerSignup";
import Guide from "@/pages/Guide";
import OneBuyDemo from "@/pages/OneBuyDemo";

import AppShell from "@/components/app/AppShell";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Deliverables from "@/pages/Deliverables";
import Payments from "@/pages/Payments";
import Influencers from "@/pages/Influencers";
import InfluencerKYS from "@/pages/InfluencerKYS";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Notifications from "@/pages/Notifications";
import AdminConsole from "@/pages/AdminConsole";
import AuditLog from "@/pages/AuditLog";
import Campaigns from "@/pages/Campaigns";
import AdFluenceIntake from "@/pages/AdFluenceIntake";
import EYDashboard from "@/pages/EYDashboard";
import CustomerStatusRequest from "@/pages/CustomerStatusRequest";
import AggregatePOs from "@/pages/AggregatePOs";
import Reports from "@/pages/Reports";
import CampaignDetail from "@/pages/CampaignDetail";
import RulesConfig from "@/pages/RulesConfig";
import AutoFetchMonitor from "@/pages/AutoFetchMonitor";
import ExceptionInbox from "@/pages/ExceptionInbox";
import PaymentApprovals from "@/pages/PaymentApprovals";
import UsageScenarios from "@/pages/UsageScenarios";
import ControlTower from "@/pages/ControlTower";
import Integrations from "@/pages/Integrations";
import Readiness from "@/pages/Readiness";
import CreatorInvoices from "@/pages/CreatorInvoices";

function RequireAuth({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeForRole(user.role)} replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/influencer-signup" element={<InfluencerSignup />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/onebuy" element={<OneBuyDemo />} />
          <Route path="/onebuy/:tenant" element={<OneBuyDemo />} />

          {/* CUSTOMER (FMCG) — read-only + status-request in AdFluence-first flow */}
          <Route path="/customer" element={<RequireAuth roles={["customer"]}><AppShell /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="reports" element={<Reports />} />
            <Route path="autofetch" element={<AutoFetchMonitor />} />
            <Route path="exceptions" element={<ExceptionInbox />} />
            <Route path="payments-memos" element={<PaymentApprovals />} />
            <Route path="status-request" element={<CustomerStatusRequest />} />
            <Route path="rules" element={<RulesConfig />} />
            <Route path="admin" element={<AdminConsole />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="scenarios" element={<UsageScenarios />} />
            <Route path="tower" element={<ControlTower />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="readiness" element={<Readiness />} />
          </Route>

          {/* PROCUREMENT DESK */}
          <Route path="/procurement" element={<RequireAuth roles={["procurement_desk"]}><AppShell /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/new" element={<AdFluenceIntake />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            <Route path="aggregate-pos" element={<AggregatePOs />} />
            <Route path="aggregate-pos/:id" element={<AggregatePOs />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="influencers" element={<Influencers />} />
            <Route path="compliance" element={<Influencers />} />
            <Route path="pos" element={<PurchaseOrders />} />
            <Route path="payments" element={<Payments />} />
            <Route path="reports" element={<Reports />} />
            <Route path="autofetch" element={<AutoFetchMonitor />} />
            <Route path="exceptions" element={<ExceptionInbox />} />
            <Route path="payments-memos" element={<PaymentApprovals />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="scenarios" element={<UsageScenarios />} />
            <Route path="tower" element={<ControlTower />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="readiness" element={<Readiness />} />
          </Route>

          {/* EY AUDITOR — reviews and approves deliverables on behalf of FMCG */}
          <Route path="/ey" element={<RequireAuth roles={["ey_auditor"]}><AppShell /></RequireAuth>}>
            <Route index element={<EYDashboard />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            <Route path="deliverables" element={<Deliverables />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="reports" element={<Reports />} />
            <Route path="autofetch" element={<AutoFetchMonitor />} />
            <Route path="exceptions" element={<ExceptionInbox />} />
            <Route path="payments-memos" element={<PaymentApprovals />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="scenarios" element={<UsageScenarios />} />
            <Route path="tower" element={<ControlTower />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="readiness" element={<Readiness />} />
          </Route>

          {/* INFLUENCER */}
          <Route path="/influencer" element={<RequireAuth roles={["influencer"]}><AppShell /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="kys" element={<InfluencerKYS />} />
            <Route path="pos" element={<PurchaseOrders />} />
            <Route path="deliverables" element={<Deliverables />} />
            <Route path="payments" element={<Payments />} />
            <Route path="invoices" element={<CreatorInvoices />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="scenarios" element={<UsageScenarios />} />
            <Route path="tower" element={<ControlTower />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="readiness" element={<Readiness />} />
          </Route>

          {/* PROPAY ADMIN — L3 support with full access */}
          <Route path="/admin" element={<RequireAuth roles={["propay_admin"]}><AppShell /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            <Route path="aggregate-pos" element={<AggregatePOs />} />
            <Route path="aggregate-pos/:id" element={<AggregatePOs />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="influencers" element={<Influencers />} />
            <Route path="compliance" element={<Influencers />} />
            <Route path="pos" element={<PurchaseOrders />} />
            <Route path="deliverables" element={<Deliverables />} />
            <Route path="payments" element={<Payments />} />
            <Route path="reports" element={<Reports />} />
            <Route path="autofetch" element={<AutoFetchMonitor />} />
            <Route path="exceptions" element={<ExceptionInbox />} />
            <Route path="payments-memos" element={<PaymentApprovals />} />
            <Route path="customers" element={<AdminConsole />} />
            <Route path="rules" element={<RulesConfig />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="scenarios" element={<UsageScenarios />} />
            <Route path="tower" element={<ControlTower />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="readiness" element={<Readiness />} />
          </Route>

          {/* HUL APPROVER — payment memo sign-off */}
          <Route path="/approver" element={<RequireAuth roles={["hul_approver"]}><AppShell /></RequireAuth>}>
            <Route index element={<PaymentApprovals />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            <Route path="exceptions" element={<ExceptionInbox />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="scenarios" element={<UsageScenarios />} />
            <Route path="tower" element={<ControlTower />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="readiness" element={<Readiness />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
